import React from 'react';
import { gql, useApolloClient } from "@apollo/client";
import { stringify } from 'csv-stringify/browser/esm/sync';

import WordpressEmbedLayout from '../../layouts/WordpressEmbedLayout';
import SearchBox from '../../components/SearchBox';
import { formatPriceRange } from '../../helpers/formatting';
import './Home.scss';

const GET_ALL_FACILITIES = gql`
  query GetAllFacilities {
    facilities {
      accommodations {
        is_private
        monthly_price_end
        monthly_price_start
        notes
        price_notes
      }
      address
      admin_name
      capacity
      care_charges
      ccl_facility_id
      ccl_link
      city
      dementia
      email
      hospice
      name
      num_locked_memory_units
      pets_allowed
      phone_number
      state
      zipcode
    }
  }
`;

const HEADERS = [
  { label: 'Facility', key: 'name' },
  { label: 'Locked Memory Units', key: 'num_locked_memory_units' },
  { label: 'Address', key: 'address' },
  { label: 'City', key: 'city' },
  { label: 'State', key: 'state' },
  { label: 'Zipcode', key: 'zipcode' },
  { label: 'Phone', key: 'phone_number' },
  { label: '# Beds', key: 'capacity' },
  { label: 'Administrator', key: 'admin_name' },
  { label: 'Licensee Email', key: 'email' },
  { label: 'Pets Allowed (Y or N)', key: 'pets_allowed' },
  { label: 'Semi-Private Room Cost', key: 'semi_private_price' },
  { label: 'Semi-Private Room Price Notes', key: 'semi_private_price_notes' },
  { label: 'Semi-Private Room Notes', key: 'semi_private_notes' },
  { label: 'Private Room Cost', key: 'private_price' },
  { label: 'Private Room Price Notes', key: 'private_price_notes' },
  { label: 'Private Room Notes', key: 'private_notes' },
  { label: 'Hospice (Y or N)', key: 'hospice' },
  { label: 'Dementia (ALL, MILD, or NONE)', key: 'dementia' },
  { label: 'Care Charges (Y or N)', key: 'care_charges' },
  { label: 'CCL Link', key: 'ccl_link' }
];

const Home = (props) => {
  const client = useApolloClient();

  const fetchFacilitiesCsvData = async () => {
    const facilityFields = [
      'address',
      'admin_name',
      'capacity',
      'care_charges',
      'ccl_facility_id',
      'ccl_link',
      'city',
      'dementia',
      'email',
      'hospice',
      'name',
      'num_locked_memory_units',
      'pets_allowed',
      'phone_number',
      'state',
      'zipcode'
    ];
    return client.query({ query: GET_ALL_FACILITIES }).then((results) => {
      const facilities = [];
      results.data.facilities.forEach((facilityData) => {
        const facility = {};
        facilityFields.forEach((field) => {
          facility[field] = facilityData[field] ?? '';
        });
        (facilityData.accommodations ?? []).forEach((accommodation) => {
          const fieldPrefix = accommodation.is_private ? 'private' : 'semi_private';
          facility[`${fieldPrefix}_price`] = formatPriceRange(accommodation.monthly_price_start, accommodation.monthly_price_end);
          ['notes', 'price_notes'].forEach((field) => {
            if (accommodation[field]) {
              facility[`${fieldPrefix}_${field}`] = accommodation[field];
            }
          });
        });
        facilities.push(facility);
      });
      return facilities;
    });
  };

  const handlePrepareAndDownloadCsv = async () => {
    const facilitiesCsvData = await fetchFacilitiesCsvData();
    const csv = [
      HEADERS.map((header) => header.label),
      ...(
        facilitiesCsvData.map((data) => {
          const row = [];
          HEADERS.forEach((header) => {
            const key = header.key;
            const value = typeof(data[key]) === 'boolean' ? (data[key] === true ? 'Y' : 'N') : data[key];
            row.push(value ?? '');
          });
          return row;
        })
      )
    ];
    const blob = new Blob([stringify(csv)], { type: 'text/csv;charset=utf-8;' });
    const filename = 'sasnb_facilities.csv';
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      const a = document.createElement('a');
      if (a.download !== undefined) {
        const url = URL.createObjectURL(blob);
        a.setAttribute('href', url);
        a.setAttribute('download', filename);
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    }
  };

  return (
    <WordpressEmbedLayout>
      <div className="heading-icon">
        <img src="https://senioradvocacyservices.org/wp-content/plugins/gavias-oxpitan-themer/elementor/assets/images/icon-heading.png" alt="Welcome" />
      </div>
      <h2 className="title">
        <span>Find and compare assisted living<br />care facilities near you.</span>
      </h2>
      <SearchBox />
      <button className="link" onClick={handlePrepareAndDownloadCsv}>
        Download all facilities as a CSV
      </button>
    </WordpressEmbedLayout>
  );
};

export default Home;
